import { MessageSquare, Headphones, Clock, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import WaveAnimation from '../components/WaveAnimation';

const benefits = [
  {
    icon: MessageSquare,
    title: '24/7 AI Assistance',
    description: 'Ask any question during your learning journey—our AI-powered chat and voice system is always ready to provide instant, accurate answers.',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    icon: Brain,
    title: 'Interactive Learning Support',
    description: 'Feel like you have a personal tutor guiding you, ensuring you never feel alone while learning.',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    icon: Headphones,
    title: 'Real-Time Engagement',
    description: 'Whether through chat or voice, receive immediate responses tailored to your course and learning needs.',
    gradient: 'from-green-500 to-emerald-500'
  },
  {
    icon: Clock,
    title: 'Continuous Guidance',
    description: 'Our AI adapts to your pace and preferences, offering support exactly when you need it.',
    gradient: 'from-orange-500 to-red-500'
  }
];

export default function BenefitsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Platform Benefits
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Discover how our AI-powered platform transforms your learning experience by providing 
            real-time assistance and an interactive, teacher-like presence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-gray-800/50 backdrop-blur-sm rounded-lg p-8 hover:bg-gray-800/70 transition-all duration-300 overflow-hidden"
            >
              {/* Animated gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-gray-700/50 rounded-lg">
                    <benefit.icon className={`w-8 h-8 bg-gradient-to-br ${benefit.gradient} bg-clip-text text-transparent`} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{benefit.title}</h3>
                </div>
                
                <p className="text-gray-300 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-xl text-purple-200 mb-8">
            Experience learning like never before—interactive, engaging, and fully supported.
          </p>
          <button 
            onClick={() => navigate('/courses')}
            className="inline-flex items-center px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
          >
            Start Your Learning Journey
          </button>
        </div>
      </div>
    </div>
  );
}